import React from 'react';

import {
  PageProps,
  PagePropsWithId,
} from '@fastlessjs/backoffice-layer/dist/components/pages/types';
import { isCurrentOrganizationSuperAdmin } from '@fastlessjs/backoffice-layer/dist/hooks/useMultiTenancy';
import { lazyWithRetry } from '@fastlessjs/backoffice-layer/dist/utils/lazyWithRetry';

import Dashboard from '../dashboard/Dashboard';
import Order from '../orders/views/Detail';
import Orders from '../orders/views/Show';
import ShowReturn from '../returns/views/Show';
import ShowTraceReturn from '../trace/views/Details';
import ShowTrace from '../trace/views/List';

const MaintenanceBlockedPage = lazyWithRetry(() =>
  import(
    '@fastlessjs/backoffice-layer/dist/components/pages/Maintenance/MaintenanceBlockedPage'
  ).then((module) => ({
    default: module.default || module,
  })),
);
const MaintenanceModePage = lazyWithRetry(() =>
  import(
    '@fastlessjs/backoffice-layer/dist/components/pages/Maintenance/MaintenanceModePage'
  ).then((module) => ({
    default: module.default || module,
  })),
);
const FeedbacksList = lazyWithRetry(() =>
  import('../feedbacks/views/List').then((module) => ({
    default: module.default || module,
  })),
);
const FeedbacksUpsert = lazyWithRetry(() =>
  import('../feedbacks/views/Upsert').then((module) => ({
    default: module.default || module,
  })),
);
const FeedbacksView = lazyWithRetry(() =>
  import('../feedbacks/views/View').then((module) => ({
    default: module.default || module,
  })),
);
const ReturnFailuresOverview = lazyWithRetry(() =>
  import('../admin/ReturnFailuresOverview').then((module) => ({
    default: module.default || module,
  })),
);
const ReassortLotsPage = lazyWithRetry(() =>
  import('../reassort-lots/ReassortLotsPage').then((module) => ({
    default: module.default || module,
  })),
);
const ReassortLotsAddPage = lazyWithRetry(() =>
  import('../reassort-lots/ReassortLotsAddPage').then((module) => ({
    default: module.default || module,
  })),
);

const SuperAdminGuard = ({ children }: { children: React.ReactNode }) => {
  if (!isCurrentOrganizationSuperAdmin()) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>This page is only available in the superadmin organization.</p>
      </div>
    );
  }
  return React.createElement(React.Fragment, null, children);
};

const Routes = (base: any): any => {
  const routes = {
    ...base,
    '/maintenance': () => <MaintenanceBlockedPage />,
    '/superadmin/maintenance-mode': () => (
      <SuperAdminGuard>
        <MaintenanceModePage />
      </SuperAdminGuard>
    ),
    '/:org/feedbacks': ({ org }: PageProps) => (
      <SuperAdminGuard>
        <FeedbacksList org={org} />
      </SuperAdminGuard>
    ),
    '/:org/feedbacks/new': ({ org }: PageProps) => (
      <SuperAdminGuard>
        <FeedbacksUpsert org={org} />
      </SuperAdminGuard>
    ),
    '/:org/feedbacks/:id/edit': ({ org, id }: PagePropsWithId) => (
      <SuperAdminGuard>
        <FeedbacksUpsert org={org} id={id} />
      </SuperAdminGuard>
    ),
    '/:org/feedbacks/:id/view': ({ org, id }: PagePropsWithId) => (
      <SuperAdminGuard>
        <FeedbacksView org={org} id={id} />
      </SuperAdminGuard>
    ),
    '/:org/size-feedbacks': ({ org }: PageProps) => (
      <SuperAdminGuard>
        <FeedbacksList org={org} />
      </SuperAdminGuard>
    ),
    '/:org/size-feedbacks/new': ({ org }: PageProps) => (
      <SuperAdminGuard>
        <FeedbacksUpsert org={org} />
      </SuperAdminGuard>
    ),
    '/:org/size-feedbacks/:id/edit': ({ org, id }: PagePropsWithId) => (
      <SuperAdminGuard>
        <FeedbacksUpsert org={org} id={id} />
      </SuperAdminGuard>
    ),
    '/:org/size-feedbacks/:id/view': ({ org, id }: PagePropsWithId) => (
      <SuperAdminGuard>
        <FeedbacksView org={org} id={id} />
      </SuperAdminGuard>
    ),
    '/:org/dashboard': ({ org }: PageProps) => (
      <SuperAdminGuard>
        <Dashboard org={org} />
      </SuperAdminGuard>
    ),
    '/:org/return-failures': ({ org }: PageProps) => (
      <SuperAdminGuard>
        <ReturnFailuresOverview org={org} />
      </SuperAdminGuard>
    ),
    '/:org/reassort-lots': ({ org }: PageProps) => (
      <SuperAdminGuard>
        <ReassortLotsPage org={org} />
      </SuperAdminGuard>
    ),
    '/:org/reassort-lots/add': ({ org }: PageProps) => (
      <SuperAdminGuard>
        <ReassortLotsAddPage org={org} />
      </SuperAdminGuard>
    ),

    // ORDERS
    '/:org/orders': ({ org }: PageProps) => <Orders org={org} />,
    '/:org/orders/:id': ({ org, id }: PagePropsWithId) => <Order org={org} id={id} />,
    '/:org/orders/:id/view': ({ org, id }: PagePropsWithId) => (
      <Order org={org} id={id} />
    ),

    // RETURNS
    '/:org/returns': ({ org }: PageProps) => <ShowReturn org={org} />,

    // TRACE
    '/:org/suivi': ({ org }: PageProps) => <ShowTrace org={org} />,
    '/:org/suivi/:id': ({ org, id }: PageProps) => <ShowTraceReturn org={org} id={id} />,
    '/:org/suivi/:id/view': ({ org, id }: PageProps) => (
      <ShowTraceReturn org={org} id={id} />
    ),
  };

  return routes;
};

export default Routes;
